import {
  AppWindow,
  CircleEllipsis,
  GitBranch,
  LayoutPanelTop,
  Mail,
  MessageCircle,
} from 'lucide-react';
import { ComponentType, CSSProperties } from 'react';
import {
  SiCaldotcom,
  SiCalendly,
  SiDiscord,
  SiHubspot,
  SiInstagram,
  SiMessenger,
  SiShopify,
  SiStripe,
  SiTelegram,
  SiWhatsapp,
  SiZendesk,
} from 'react-icons/si';
import { IndexPath } from '@/Config/app/router/index.path';
import { BrandGlyph } from '@/Domain/components/Action/ActionBox/BrandGlyph';
import { catalogEntryByName } from '@/Domain/components/Action/RemoteMCPAction/mcpCatalog';
import { IntercomIcon } from '@/Domain/components/icons/Intercom.icon';
import { McpIcon } from '@/Domain/components/icons/Mcp.icon';
import { SlackIcon } from '@/Domain/components/icons/Slack.icon';
import { cn } from '@/Domain/utils/cn';
import { WEBSITE_VIEW_QUERY_PARAM, WebsiteView } from '@/UI/Website/viewMode';

type Glyph = ComponentType<{ className?: string; style?: CSSProperties }>;

export const OPEN_MODAL_QUERY_PARAM = 'open';

export const CHANNEL_CHAT_WIDGET = 'chat_widget';
export const CHANNEL_CHAT_EMBED = 'chat_embed';
export const CHANNEL_CHAT_PAGE = 'chat_page';
export const CHANNEL_SHOPIFY = 'shopify';
export const CHANNEL_HUBSPOT = 'hubspot';
export const CHANNEL_INTERCOM = 'intercom';
export const CHANNEL_WHATSAPP = 'whatsapp';
export const CHANNEL_INSTAGRAM = 'instagram';
export const CHANNEL_MESSENGER = 'messenger';
export const CHANNEL_TELEGRAM = 'telegram';
export const CHANNEL_DISCORD = 'discord';
export const CHANNEL_DISCORD_ADVANCED = 'discord_advanced';
export const CHANNEL_SLACK = 'slack';
export const CHANNEL_ZENDESK = 'zendesk';
export const CHANNEL_EMAIL = 'email';
export const CHANNEL_MCP = 'mcp';
export const CHANNEL_API = 'api';
export const CHANNEL_OTHER_ID = 'other';

export const APP_HUBSPOT = 'hubspot';
export const APP_SHOPIFY = 'shopify';
export const APP_GOOGLE_SHEETS = 'google_sheets';
export const APP_STRIPE = 'stripe';
export const APP_CALDOTCOM = 'caldotcom';
export const APP_CALENDLY = 'calendly';
export const APP_OTHER_MCP = 'other_mcp';
export const APP_ATLASSIAN = 'atlassian';
export const APP_NOTION = 'notion';
export const APP_LINEAR = 'linear';
export const APP_OTHER_API_ID = 'other_api';

const BROWSE_ALL_LABEL_KEY = 'getting_started:external_apps_step.browse_catalog';
const OTHER_APP_LABEL_KEY = 'getting_started:external_apps_step.other_app_label';

const ApiGlyph: Glyph = ({ className, style }) => <GitBranch className={className} style={style} />;

const McpGlyph: Glyph = ({ className, style }) => <McpIcon className={className} style={style} />;

// react-icons/si only ships the full Cal.com wordmark; clip ".com" via overflow-hidden.
const CalMark: Glyph = ({ className, style }) => (
  <span
    className={cn('inline-flex items-center overflow-hidden flex-shrink-0', className)}
    style={{ ...style, width: '19px', height: '16px' }}
  >
    <SiCaldotcom style={{ height: '48px', width: '48px', flexShrink: 0 }} />
  </span>
);

export interface ChannelOption {
  id: string;
  label: string;
  icon: Glyph;
  // Optional: Domain/components/icons/* use baked-in fills and ignore color.
  iconColor?: string;
  routePath: string;
  /** When true, "Set up" appends ?open=<id> for the destination page's auto-open handler. */
  autoOpenModal: boolean;
  /** Extra query-string params merged into the destination URL (e.g. view=embed). */
  queryParams?: Record<string, string>;
}

export const CHANNELS: readonly ChannelOption[] = [
  {
    id: CHANNEL_CHAT_WIDGET,
    label: 'Chat widget',
    icon: MessageCircle,
    routePath: IndexPath.website,
    autoOpenModal: false,
  },
  {
    id: CHANNEL_CHAT_EMBED,
    label: 'Chat embed',
    icon: AppWindow,
    routePath: IndexPath.website,
    autoOpenModal: false,
    queryParams: { [WEBSITE_VIEW_QUERY_PARAM]: WebsiteView.Embed },
  },
  {
    id: CHANNEL_CHAT_PAGE,
    label: 'Chat page',
    icon: LayoutPanelTop,
    routePath: IndexPath.website,
    autoOpenModal: false,
    queryParams: { [WEBSITE_VIEW_QUERY_PARAM]: WebsiteView.ChatPage },
  },
  {
    id: CHANNEL_SHOPIFY,
    label: 'Shopify',
    icon: SiShopify,
    iconColor: '#95BF47',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_HUBSPOT,
    label: 'HubSpot',
    icon: SiHubspot,
    iconColor: '#FF7A59',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_INTERCOM,
    label: 'Intercom',
    icon: IntercomIcon,
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_WHATSAPP,
    label: 'WhatsApp',
    icon: SiWhatsapp,
    iconColor: '#25D366',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_INSTAGRAM,
    label: 'Instagram',
    icon: SiInstagram,
    iconColor: '#E4405F',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_MESSENGER,
    label: 'Messenger',
    icon: SiMessenger,
    iconColor: '#00B2FF',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_TELEGRAM,
    label: 'Telegram',
    icon: SiTelegram,
    iconColor: '#26A5E4',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_DISCORD,
    label: 'Discord',
    icon: SiDiscord,
    iconColor: '#5865F2',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_SLACK,
    label: 'Slack',
    icon: SlackIcon,
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_ZENDESK,
    label: 'Zendesk',
    icon: SiZendesk,
    iconColor: '#03363D',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_EMAIL,
    label: 'Email',
    icon: Mail,
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_MCP,
    label: 'MCP',
    icon: McpGlyph,
    iconColor: '#EF4444',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_API,
    label: 'API',
    icon: ApiGlyph,
    iconColor: '#EF4444',
    routePath: IndexPath.channels,
    autoOpenModal: true,
  },
  {
    id: CHANNEL_OTHER_ID,
    label: 'Other',
    icon: CircleEllipsis,
    routePath: IndexPath.channels,
    // No dedicated SPA setup sheet: "Set up" lands on the channels overview instead of a no-op deep link.
    autoOpenModal: false,
  },
] as const;

export interface AppOption {
  id: string;
  label: string;
  labelKey?: string;
  icon: Glyph;
  iconColor?: string;
  routePath: string;
  autoOpenModal: boolean;
  catalogName?: string;
}

// Renaming an id orphans persisted selections because PostOnboardingChecklist drops unknown ids.
const catalogAppId = (catalogName: string): string =>
  `mcp_${catalogName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')}`;

const catalogGlyph = (icon: string, name: string): Glyph => {
  const CatalogGlyph: Glyph = ({ className, style }) => (
    <span
      className={cn('inline-flex items-center justify-center flex-shrink-0', className)}
      style={style}
    >
      <BrandGlyph icon={icon} name={name} size={16} />
    </span>
  );
  return CatalogGlyph;
};

const catalogApp = (catalogName: string, id?: string): AppOption => ({
  id: id ?? catalogAppId(catalogName),
  label: catalogName,
  icon: catalogGlyph(catalogEntryByName(catalogName)?.icon ?? '', catalogName),
  routePath: IndexPath.actions,
  autoOpenModal: true,
  catalogName,
});

export const APPS: readonly AppOption[] = [
  {
    id: APP_HUBSPOT,
    label: 'HubSpot',
    icon: SiHubspot,
    iconColor: '#FF7A59',
    routePath: IndexPath.actions,
    autoOpenModal: true,
  },
  {
    id: APP_SHOPIFY,
    label: 'Shopify',
    icon: SiShopify,
    iconColor: '#95BF47',
    routePath: IndexPath.actions,
    autoOpenModal: true,
  },
  {
    id: APP_STRIPE,
    label: 'Stripe',
    icon: SiStripe,
    iconColor: '#635BFF',
    routePath: IndexPath.actions,
    autoOpenModal: true,
    catalogName: 'Stripe',
  },
  catalogApp('Notion', APP_NOTION),
  {
    id: APP_CALDOTCOM,
    label: 'Cal.com',
    icon: CalMark,
    iconColor: 'var(--foreground)',
    routePath: IndexPath.actions,
    autoOpenModal: true,
    catalogName: 'Cal.com',
  },
  {
    id: APP_CALENDLY,
    label: 'Calendly',
    icon: SiCalendly,
    iconColor: '#006BFF',
    routePath: IndexPath.actions,
    autoOpenModal: true,
    catalogName: 'Calendly',
  },
  // The shortlist is capped by how it reads on a phone, not by what the catalog carries: at 375px
  // these wrap to roughly nine rows, and every row past that makes the step harder to scan rather
  // than more useful. Anything left out is still one tap away behind the browse pill, so the bar
  // for adding one here is evidence that people ask for it — Gorgias, ClickUp and AfterShip were
  // dropped for having zero write-ins across the 209 free-text rows in production.
  catalogApp('Pipedrive'),
  catalogApp('Klaviyo'),
  catalogApp('Airtable'),
  catalogApp('PayPal'),
  catalogApp('Canva'),
  catalogApp('monday'),
  catalogApp('Attio'),
  catalogApp('Wix'),
  // Linear / Atlassian (and Notion above) keep the ids #5357 shipped, not the derived `mcp_*` ones:
  // `saasHostRouter` defers a failed scrape of those hosts into `external_apps_selection` under
  // these ids, and users have already persisted them.
  catalogApp('Linear', APP_LINEAR),
  catalogApp('Atlassian', APP_ATLASSIAN),
  catalogApp('PostHog'),
  {
    id: APP_OTHER_MCP,
    label: 'Browse the catalog',
    labelKey: BROWSE_ALL_LABEL_KEY,
    icon: McpGlyph,
    iconColor: '#EF4444',
    routePath: IndexPath.actions,
    autoOpenModal: true,
  },
  {
    id: APP_OTHER_API_ID,
    label: 'Other app',
    labelKey: OTHER_APP_LABEL_KEY,
    icon: ApiGlyph,
    iconColor: '#EF4444',
    routePath: IndexPath.actions,
    autoOpenModal: true,
  },
] as const;

// Map avoids inherited-property lookups because the `?open=` value is user-controlled.
export const ONBOARDING_APP_TO_MCP_CATALOG_NAME = new Map<string, string>(
  APPS.flatMap((app): [string, string][] => (app.catalogName ? [[app.id, app.catalogName]] : [])),
);
